import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Input, Message, Select, Space, Table, Tag } from '@arco-design/web-react';
import { get, post } from '../requestNew';

type Stock = { id: string; stockCode: string; stockName: string; starred: boolean | number; themes?: string; quoteSymbol?: string; quoteStatus?: string };
type PageResponse = { code: number; data: { list: Stock[]; total: number } };
const API = '/agent/admin/v1/star-stocks';
const isStar = (stock: Stock) => stock.starred === true || stock.starred === 1;

export default function AgentStarStockManagement() {
  const [search, setSearch] = useState('');
  const [keyword, setKeyword] = useState('');
  const [filter, setFilter] = useState('starred');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<Stock[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState('');
  const sequence = useRef(0);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; sequence.current += 1; }; }, []);
  const load = useCallback(async () => {
    const request = ++sequence.current;
    setLoading(true);
    try {
      const result = await get<PageResponse>(API, { keyword, pageNum: page, pageSize: 20,
        ...(filter === 'all' ? {} : { starred: filter === 'starred' }) });
      if (!mounted.current || request !== sequence.current) return;
      if (page > 1 && result.data.list.length === 0) { setPage(Math.max(1, Math.ceil(result.data.total / 20))); return; }
      setRows(result.data.list); setTotal(result.data.total);
    } catch {
      if (mounted.current && request === sequence.current) { setRows([]); setTotal(0); }
    } finally { if (mounted.current && request === sequence.current) setLoading(false); }
  }, [keyword, filter, page]);
  useEffect(() => { void load(); }, [load]);
  const mark = async (stock: Stock) => {
    setSaving(stock.id);
    try {
      await post(API + '/' + stock.id + '/mark', { starred: !isStar(stock) });
      if (!mounted.current) return;
      Message.success(isStar(stock) ? '已取消明星标记，下一轮成功刷新生效' : '已标记为明星，下一轮成功刷新生效');
      await load();
    } catch { /* The shared request layer displays the server error. */ }
    finally { if (mounted.current) setSaving(''); }
  };
  return <div style={{ padding: 24 }}>
    <h2 style={{ marginTop: 0 }}>美股明星个股管理</h2>
    <Alert type="info" content="已标记的明星个股，满足任一条件即可入选：①属于所选时段涨幅前三或跌幅前三板块，且涨跌幅绝对值大于3%；②不限前三板块，涨跌幅绝对值大于5%。合并去重后按涨跌幅从高到低展示，最多10只；无有效行情不展示。保存后在下一轮成功刷新生效。" style={{ marginBottom: 20 }} />
    <Space style={{ marginBottom: 16 }}>
      <Input.Search aria-label="搜索股票" placeholder="股票代码或名称" value={search} maxLength={100} style={{ width: 280 }}
        disabled={!!saving} onChange={setSearch} onSearch={() => { setPage(1); setKeyword(search.trim()); }} />
      <Select aria-label="明星状态" value={filter} disabled={!!saving} style={{ width: 150 }}
        onChange={value => { setPage(1); setFilter(value); }} options={[
          { label: '已标记明星', value: 'starred' }, { label: '未标记', value: 'unstarred' }, { label: '全部美股', value: 'all' }
        ]} />
      <Button disabled={!!saving} onClick={() => { void load(); }}>刷新</Button>
    </Space>
    <Table<Stock> rowKey="id" data={rows} loading={loading} pagination={{ current: page, total, pageSize: 20, disabled: !!saving, onChange: setPage }} columns={[
      { title: '股票代码', dataIndex: 'stockCode', width: 130 },
      { title: '股票名称', dataIndex: 'stockName', width: 180 },
      { title: '所属题材', dataIndex: 'themes', render: value => value || '未关联题材' },
      { title: '行情状态', render: (_, row) => !row.quoteSymbol ? '未配置行情代码' : row.quoteStatus || '待校验', width: 150 },
      { title: '明星标记', render: (_, row) => <Tag color={isStar(row) ? 'gold' : 'gray'}>{isStar(row) ? '明星个股' : '未标记'}</Tag>, width: 110 },
      { title: '操作', width: 130, render: (_, row) => <Button type="text" loading={saving === row.id} disabled={!!saving || loading}
          onClick={() => { void mark(row); }}>{isStar(row) ? '取消标记' : '标记为明星'}</Button> }
    ]} />
  </div>;
}
